import { Dialog, DialogContent, DialogProps, CircularProgress } from '@mui/material';
import { FC, useState } from 'react';
import { InformationDialogTitle } from '../../../../../lib/components/compound/dialogs/informationDialog/title';
import { Button } from '../../../../../lib/components/atomic/button';
import { DatePicker } from '../../../../../lib/components';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { triggerPersonioPayrollSyncMutation } from '../../../../../queries/company/personio';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

type Props = {
  companyId: string;
  onClose?: () => void;
} & Pick<DialogProps, 'open'>;

export const TriggerSyncDialog: FC<Props> = ({ open, companyId, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [date, setDate] = useState<Date>(new Date());
  const [error, setError] = useState(false);

  const { mutate: triggerSync, isPending } = triggerPersonioPayrollSyncMutation({
    onSuccess: (data) => {
      const isRestarted = data.message?.includes('restarted');
      enqueueSnackbar(isRestarted ? t('export:SYNC_RESTARTED') : t('export:SYNC_STARTED'), {
        variant: 'success'
      });
      queryClient.invalidateQueries({ queryKey: ['company', companyId, 'personio', 'sync'] });
      onClose?.();
    },
    onError: () => {
      enqueueSnackbar(t('tasks:SYNC_ERROR'), {
        variant: 'error'
      });
      queryClient.invalidateQueries({ queryKey: ['company', companyId, 'personio', 'sync'] });
      onClose?.();
    }
  });

  const handleConfirm = () => {
    const accountingPeriod = format(date, 'yyyy-MM');
    triggerSync({
      companyId,
      accountingPeriod
    });
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: 'flex flex-col justify-center'
      }}
    >
      <InformationDialogTitle title={t('export:START_SYNC_DIALOG_TITLE')} onClose={handleClose} />
      <DialogContent>
        <p className="mb-4 text-gray-600">{t('export:START_SYNC_DIALOG_TEXT')}</p>
        <DatePicker
          variant="month"
          onChange={(newDate) => {
            setError(false);
            setDate(new Date(newDate));
          }}
          onChangeInvalidDate={() => setError(true)}
          error={error}
          initialValue={format(date, 'yyyy-MM-dd')}
        />

        <div className="flex justify-between gap-4 mt-4 mb-4">
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            {t('common:CANCEL')}
          </Button>
          <Button onClick={handleConfirm} disabled={isPending ?? error}>
            {isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('export:START_SYNC_DIALOG_CONFIRM')
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
